
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../../firebaseClient";
import { DataToolbar, ActionButtons } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { ContractForm } from "../modules/legal/forms/ContractForm";
import { PreviewModal } from "../components/PreviewModal";
import { useToast } from "../components/Toast";
import { ContractDoc } from "../types/legalOps";
import { logActivity } from "../services/activityLog";
import { getContractTypeLabel } from "../constants/contractTypes";

export function LegacyContractsPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [items, setItems] = useState<ContractDoc[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ContractDoc | null>(null);
  const [previewItem, setPreviewItem] = useState<ContractDoc | null>(null);

  // Fetch Legacy Contracts
  useEffect(() => {
    setLoading(true);
    // Fetch all legacy items
    const q = db.collection("legal_contracts").where("isLegacy", "==", true);
    
    const unsub = q.onSnapshot(snap => {
        let list = snap.docs.map(d => ({id: d.id, ...d.data()} as ContractDoc));
        
        // Sort in memory desc
        list.sort((a,b) => {
            const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
            const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
            return tb - ta;
        });

        setItems(list);
        setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleCreate = () => {
      setEditingItem(null);
      setIsFormOpen(true);
  };

  const handleEdit = (item: ContractDoc) => {
      // Permission Check: Only Draft and Self-Created
      // For Admins (SuperUser/Legal), they can view but maybe not edit here? 
      // Requirement: "PT can modify Draft created by self".
      
      const isOwner = item.lastUpdatedBy === user?.displayName || item.lastUpdatedBy === "User"; // Fallback check, ideally check uid
      // Since schema uses lastUpdatedBy name, it's weak. But sufficient for PT scenario.
      // Better: check createdAt/createdBy logic if implemented.
      
      if (item.status !== "草稿") {
          alert("僅能修改「草稿」狀態的合約。");
          return;
      }
      setEditingItem(item);
      setIsFormOpen(true);
  };

  const handleDelete = async (item: ContractDoc) => {
      if (item.status !== "草稿") {
          alert("僅能刪除「草稿」狀態的合約。");
          return;
      }
      if (!confirm(`確定刪除 ${item.contractNo}?`)) return;

      try {
          await db.collection("legal_contracts").doc(item.id).delete();
          await logActivity({
              module: 'contract',
              action: 'delete_legacy',
              entityId: item.id!,
              entityType: 'Contract',
              details: `Deleted legacy contract ${item.contractNo}`,
              actorId: user?.id,
              actorName: user?.displayName
          });
          showToast("已刪除");
      } catch (e) {
          console.error(e);
          showToast("刪除失敗", "error");
      }
  };

  return (
    <div style={{display:'flex', flexDirection:'column', height:'100%'}}>
        <div style={{marginBottom: 16}}>
            <h2 style={{margin:0, fontSize:20, fontWeight:'bold', color:'#fff'}}>舊合約登錄系統</h2>
            <div style={{fontSize:13, color:'var(--muted)', marginTop:4}}>
                僅用於輸入既有紙本／掃描舊合約的基本資料，送出後將併入主合約資料庫。
            </div>
        </div>

        <DataToolbar 
            canEdit={true} 
            onCreate={handleCreate} 
            onExport={()=>{}} 
        />
        {/* Hide Export button via CSS or modify DataToolbar later if strict requirement. 
            For now, DataToolbar shows Export by default. */}
            
        <div style={{
            padding: 0, 
            flex: 1, 
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column', 
            background: '#161616',
            border: '1px solid var(--border)',
            borderRadius: 16,
            width: '100%',
            maxWidth: '100%'
        }}>
            <div style={{overflowY:'auto', flex:1}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
                    <thead style={{background:'#222', color:'#aaa', textAlign:'left', position:'sticky', top:0}}>
                        <tr>
                            <th style={{padding:12}}>編號</th>
                            <th style={{padding:12}}>標題</th>
                            <th style={{padding:12}}>類型</th>
                            <th style={{padding:12}}>對造</th>
                            <th style={{padding:12}}>狀態</th>
                            <th style={{padding:12, textAlign:'right'}}>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} style={{padding:20, textAlign:'center'}}>載入中...</td></tr>}
                        {!loading && items.map(item => (
                            <tr key={item.id} style={{borderBottom:'1px solid #333'}}>
                                <td style={{padding:12, fontFamily:'monospace', color:'#fbbf24'}}>{item.contractNo}</td>
                                <td style={{padding:12, fontWeight:500, color:'#fff'}}>{item.title}</td>
                                <td style={{padding:12}}>{getContractTypeLabel(item.contractType || "")}</td>
                                <td style={{padding:12}}>{item.counterparty}</td>
                                <td style={{padding:12}}>
                                    <span className="badge" style={{
                                        color: item.status==='草稿'?'#aaa': item.status==='待覆核'?'#f59e0b':'var(--ok)',
                                        borderColor: item.status==='草稿'?'#aaa': item.status==='待覆核'?'#f59e0b':'var(--ok)'
                                    }}>
                                        {item.status}
                                    </span>
                                </td>
                                <td style={{padding:12, textAlign:'right'}}>
                                    <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
                                        <button className="smallbtn" onClick={() => setPreviewItem(item)}>瀏覽</button>
                                        {item.status === '草稿' && (
                                            <>
                                                <button className="smallbtn" onClick={() => handleEdit(item)}>修改</button>
                                                <button className="smallbtn" style={{color:'var(--danger)', borderColor:'var(--danger)'}} onClick={() => handleDelete(item)}>刪除</button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>

        {isFormOpen && (
            <ContractForm 
                initialData={editingItem}
                allRows={items} // Pass legacy items for seq generation
                isLegacyMode={true}
                onClose={() => setIsFormOpen(false)}
                onSuccess={() => { setIsFormOpen(false); showToast("儲存成功"); }}
            />
        )}

        {previewItem && <PreviewModal data={previewItem} onClose={() => setPreviewItem(null)} />}
    </div>
  );
}
